import InputArea from "@/components/Chat/inputArea.component";
import MessagesScrollArea from "@/components/Chat/messagesScrollArea.component";
import CustomLoader from "@/components/CustomLoader/customLoader.component";
import { ROUTE_HOME } from "@/constants/routes.constants";
import {
  fetchMessageHistory,
  pingChatForNewMessages,
  sendMessageToChat,
  sendNewChatMessage,
} from "@/models/api/messages.model";
import {
  locForGetConversationsMessages,
  locForSetConversationsMessages,
} from "@/models/localForage/conversations.model";
import { AppContext } from "@/services/AppContext/appContext";
import { ChatMessage } from "@/types";
import { genericFailureToast } from "@/utils/utils.util";
import isEqual from "fast-deep-equal";
import "highlight.js/styles/github-dark.css"; // Or another theme
import type React from "react";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

const ChatInterfaceContainer: React.FC = () => {
  const { chatID } = useParams();
  const nav = useNavigate();
  const {
    setSelectedAgentModel,
    agentsModels,
    selectedAgentModel,
    conversations,
    addConversation,
    refreshAllConversations,
    updateConversation,
  } = useContext(AppContext);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sendingMessage, setSendingMessage] = useState<ChatMessage | null>(
    null
  );
  const [waitingForResponse, setWaitingForResponse] = useState(false);

  useEffect(() => {
    const fetchChatMessages = async () => {
      setLoading(true);
      setError(null);
      setMessages([]);
      setSendingMessage(null);
      setWaitingForResponse(false);
      if (!chatID) {
        setLoading(false);
        return;
      }

      const numberChatID = parseInt(chatID);
      if (!numberChatID || isNaN(numberChatID)) {
        console.error("Invalid chat ID");
        genericFailureToast("Invalid chat ID");
        nav(ROUTE_HOME);
        return;
      }

      const localResponse = await locForGetConversationsMessages(numberChatID);
      if (localResponse) {
        setMessages(localResponse);
        setLoading(false);
      }

      const response = await fetchMessageHistory({
        conversationID: numberChatID,
      });

      console.log("Fetched messages:", response);

      if (response.rStatus === "error") {
        console.error("Error fetching messages:", response.rMessage);
        genericFailureToast(response.rMessage);
        setError(response.rMessage);
        setLoading(false);
        return;
      }

      if (!isEqual(response.rData, localResponse)) {
        setMessages(response.rData.messages);
      }
      await locForSetConversationsMessages(
        numberChatID,
        response.rData.messages
      );

      updateConversation(response.rData.conversation);

      setLoading(false);
    };

    fetchChatMessages();
  }, [chatID, nav, setSelectedAgentModel, updateConversation]);

  useEffect(() => {
    if (!chatID) {
      return;
    }

    if (
      !agentsModels ||
      !conversations ||
      !agentsModels.length ||
      !conversations.length
    ) {
      return;
    }

    const foundConversation = conversations.find(
      (conversation) => conversation.id === parseInt(chatID)
    );

    if (foundConversation) {
      setSelectedAgentModel(
        agentsModels.find((model) => model.id === foundConversation.agentID) ||
          null
      );
    } else {
      console.error("Conversation not found");
      genericFailureToast(
        "Conversation not found. Fetching all conversations."
      );
      refreshAllConversations();
    }
  }, [
    agentsModels,
    chatID,
    conversations,
    setSelectedAgentModel,
    refreshAllConversations,
  ]);

  async function sendMessage(markDownContent: string) {
    if (sendingMessage) {
      console.warn("Already sending a message");
      return;
    }

    if (!selectedAgentModel) {
      console.error("No agent model selected");
      genericFailureToast("No agent model selected");
      return;
    }

    console.log(markDownContent);
    // Normalize new lines to ensure only \n is used, not \\\n

    const normalizedContent = markDownContent;
    // .replace(/\r\n/g, "\n")
    // .replace(/\\\n/g, "\n");

    console.log("Normalized content:", normalizedContent);

    setSendingMessage({
      id: messages.length + 1,
      content: normalizedContent,
      sender: "account",
    });

    const isFistMessage = messages.length === 0;

    if (isFistMessage) {
      const response = await sendNewChatMessage({
        message: normalizedContent,
        modelID: selectedAgentModel?.id || 0,
      });
      if (response.rStatus === "error") {
        console.error("Error sending message:", response.rMessage);
        genericFailureToast(response.rMessage);
        setSendingMessage(null);
        return;
      }

      const newConversation = response.rData.conversation;
      const newMessages = response.rData.messages;
      addConversation(newConversation);
      nav(`${ROUTE_HOME}/${newConversation.id}`);
      // setSendingMessage(null);
      locForSetConversationsMessages(newConversation.id, newMessages);
    } else {
      const response = await sendMessageToChat({
        conversationID: parseInt(chatID || "0"),
        message: normalizedContent,
        modelID: selectedAgentModel?.id || 0,
      });

      if (response.rStatus === "error") {
        console.error("Error sending message:", response.rMessage);
        genericFailureToast(response.rMessage);
        setSendingMessage(null);
        return;
      }

      const newMessages = response.rData.messages;
      setMessages((prevMessages) => [...prevMessages, ...newMessages]);
      setSendingMessage(null);
      await locForSetConversationsMessages(parseInt(chatID || "0"), [
        ...messages,
        ...newMessages,
      ]);

      updateConversation(response.rData.conversation);
    }
  }

  useEffect(() => {
    if (sendingMessage !== null) {
      setWaitingForResponse(false);

      return;
    }

    if (!messages.length) {
      setWaitingForResponse(false);
      return;
    }

    // if the last message is from the account, set the waitingForResponse to true
    if (messages[messages.length - 1].sender === "account") {
      setWaitingForResponse(true);
    } else {
      setWaitingForResponse(false);
    }
  }, [messages, sendingMessage]);

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    let attempt = 0;
    let cancelled = false;
    // This timer sequence is chosen to reduce server load for inactive chats while still providing quick updates for active ones.
    if (!chatID) {
      cancelled = true;
      if (timer) clearTimeout(timer);
      return;
    }

    const numberChatID = parseInt(chatID);
    if (isNaN(numberChatID)) {
      console.error("Invalid chat ID for pinging messages");
      cancelled = true;
      if (timer) clearTimeout(timer);
      return;
    }
    if (!waitingForResponse) {
      cancelled = true;
      if (timer) clearTimeout(timer);
      return; // Only start pinging if waiting for a response
    }

    const intervals = [50, 5000, 10000, 20000, 30000, 60000];
    const getInterval = () =>
      attempt < intervals.length ? intervals[attempt] : 60000;

    const pingLoop = async () => {
      if (cancelled) return;
      const result = await pingChatForNewMessages({
        conversationID: numberChatID,
      });
      console.log("Ping result:", result);
      console.log("Attempt number:", attempt + 1);
      if (cancelled) return;
      if (result.rStatus === "success") {
        // This update is performed to ensure new messages are shown as soon as they arrive.
        setMessages((prev) => {
          // Only update if there are new messages
          if (!isEqual(prev, result.rData.messages)) {
            return result.rData.messages;
          }
          return prev;
        });
        // update conversations
        updateConversation(result.rData.conversation);
        // Update local storage

        // Concatenating arrays to avoid nested array structure
        locForSetConversationsMessages(numberChatID, result.rData.messages);
        attempt = 0; // Reset timer sequence after new message
      } else {
        switch (result.rMessage) {
          case "pending":
            break;
          case "request_aborted":
            console.warn("Request aborted");
            break;
          case "conversation_id":
            console.error("Invalid conversation ID");
            genericFailureToast("Invalid conversation ID");
            nav(ROUTE_HOME);
            break;
          case "unauthorized":
            console.error("Unauthorized access");
            genericFailureToast("Unauthorized access. Please log in again.");
            nav(ROUTE_HOME);
            break;
          case "invalid_data":
            console.error("Invalid data received");
            genericFailureToast(
              "Invalid data received. Please contact support."
            );
            break;
          case "pending_not_found":
            if (attempt < 1) {
              console.warn("No pending messages found");
            } else {
              console.error("No pending messages found");
              genericFailureToast("No pending messages found.");
            }
            break;
          case "route_not_found":
            console.error("Route not found");
            genericFailureToast("Route not found. Please contact support.");
            break;
          case "network_error":
            console.error("Network error");
            genericFailureToast(
              "Network error. Please check your internet connection."
            );
            break;
          case "bad_request":
            console.error("Bad request");
            genericFailureToast("Bad request. Please contact support.");
            break;
          case "internal_server_error":
            console.error("Internal server error");
            genericFailureToast(
              "Internal server error. Please contact support."
            );
            break;
          case "failed_to_ping_chat":
            console.error("Failed to ping chat");
            genericFailureToast("Failed to ping chat. Please contact support.");
            break;
          default:
            break;
        }
        attempt++;
      }
      timer = setTimeout(pingLoop, getInterval());
    };

    // Only start pinging if waiting for a response
    if (waitingForResponse) {
      pingLoop();
    }

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [waitingForResponse, chatID]);

  return (
    <div className="flex h-full w-full flex-col bg-gray-900 p-2 pt-0">
      <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col overflow-hidden">
        {loading ? (
          <CustomLoader containerClassName="mt-8" />
        ) : error ? (
          <div className="flex h-full w-full items-center justify-center">
            <p className="text-red-500">{error}</p>
          </div>
        ) : (
          <>
            <MessagesScrollArea
              messages={messages}
              sendingMessage={sendingMessage}
              waitingForResponse={waitingForResponse}
            />
            {selectedAgentModel && (
              <InputArea
                onSendMessage={sendMessage}
                isSending={!!sendingMessage}
                waitingForResponse={waitingForResponse}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ChatInterfaceContainer;
